import type {
  AdminDocumentType,
  AdminDocumentTypesResponse,
  CreateAdminDocumentTypeBody,
  PatchAdminDocumentTypeBody,
} from '@/api/(admin)/document-types/types';
import { BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/** Pure RPC for the admin document-types page, all on the modern TS API under `BACKEND_URL`. */

/** The list. Takes a pre-built query string; paging, sort and filters live in the hook. */
export async function fetchAdminDocumentTypes(
  qs: string,
): Promise<AdminDocumentTypesResponse> {
  return apiFetch<AdminDocumentTypesResponse>(
    `/admin/document-types?${qs}`,
    {},
    BACKEND_URL,
  );
}

export async function createAdminDocumentType(
  body: CreateAdminDocumentTypeBody,
): Promise<AdminDocumentType> {
  return apiFetch<AdminDocumentType>(
    '/admin/document-types',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** A partial update — the route merges only the keys it is given. */
export async function patchAdminDocumentType(
  id: string,
  body: PatchAdminDocumentTypeBody,
): Promise<AdminDocumentType> {
  return apiFetch<AdminDocumentType>(
    `/admin/document-types/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** Deletes one. 204 on success; 409 `used_document_type` while documents reference it. */
export async function deleteAdminDocumentType(id: string): Promise<void> {
  return apiFetch<void>(
    `/admin/document-types/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    BACKEND_URL,
  );
}

/**
 * Marks a type deprecated, or takes the mark off.
 *
 * Its own endpoint, not a field on the PATCH above: that body is `.strict()`
 * and declares no `isDeprecated`, so an ordinary edit cannot hide a type.
 */
export async function updateAdminDocumentTypeUsage(
  id: string,
  deprecate: boolean,
): Promise<void> {
  return apiFetch<void>(
    `/admin/document-types/${encodeURIComponent(id)}/usage`,
    { method: 'PATCH', body: JSON.stringify({ deprecate }) },
    BACKEND_URL,
  );
}
